import router from '@/router'
import { getStoreName } from '@/base/MaUtils'
import { StoreWithAll } from '@/base/constants/Store'
import { RouteName } from '@/base/constants/route'
import { PRODUCT_FAMILY } from '@/base/constants/authorization'
import * as COUNTRY_LIST from './country-codes'

const IOS_SPECIAL_MAPPING = {
  [RouteName.APP_INTELLIGENCE_ANALYSIS_REVIEWS_REVIEW_ANALYSIS]: COUNTRY_LIST.IOS_REVIEWS_COUNTRY_CODES,
  [RouteName.APP_INTELLIGENCE_ANALYSIS_REVIEWS_MOST_MENTIONED_KEYWORDS_ANALYSIS]: COUNTRY_LIST.IOS_REVIEWS_COUNTRY_CODES,
  [RouteName.APP_INTELLIGENCE_ANALYSIS_IN_APP_EVENTS_CATEGORY_EVENTS_TAB]: COUNTRY_LIST.IN_APP_EVENTS_COUNTRY_CODES,
  [RouteName.APP_INTELLIGENCE_ANALYSIS_PROMOTIONAL_CONTENT_CATEGORY_EVENTS_TAB]: COUNTRY_LIST.PROMOTIONAL_CONTENT_COUNTRY_CODES,
  [RouteName.ASO_INTELLIGENCE_METADATA_APP_UPDATE_TIMELINE]: COUNTRY_LIST.IOS_APP_UPDATE_TIMELINE_COUNTRY_CODES,
}

const PLAY_SPECIAL_MAPPING = {
  [RouteName.APP_INTELLIGENCE_ANALYSIS_REVIEWS_REVIEW_ANALYSIS]: COUNTRY_LIST.PLAY_REVIEWS_COUNTRY_CODES,
  [RouteName.APP_INTELLIGENCE_ANALYSIS_REVIEWS_MOST_MENTIONED_KEYWORDS_ANALYSIS]: COUNTRY_LIST.PLAY_REVIEWS_COUNTRY_CODES,
  [RouteName.APP_INTELLIGENCE_ANALYSIS_IN_APP_EVENTS_CATEGORY_EVENTS_TAB]: COUNTRY_LIST.IN_APP_EVENTS_COUNTRY_CODES,
  [RouteName.APP_INTELLIGENCE_ANALYSIS_PROMOTIONAL_CONTENT_CATEGORY_EVENTS_TAB]: COUNTRY_LIST.PROMOTIONAL_CONTENT_COUNTRY_CODES,
  [RouteName.ASO_INTELLIGENCE_METADATA_APP_UPDATE_TIMELINE]: COUNTRY_LIST.PLAY_APP_UPDATE_TIMELINE_COUNTRY_CODES,
}

const IOS_APP_INTEL_DEFAULT_MAPPING = COUNTRY_LIST.IOS_APP_INTEL_COUNTRY_CODES
const PLAY_APP_INTEL_DEFAULT_MAPPING = COUNTRY_LIST.PLAY_APP_INTEL_COUNTRY_CODES

const IOS_PRODUCT_FAMILY_MAPPING = {
  [PRODUCT_FAMILY.MARKET]: COUNTRY_LIST.IOS_MARKET_INTEL_COUNTRY_CODES,
  [PRODUCT_FAMILY.ASO]: COUNTRY_LIST.IOS_ASO_INTEL_COUNTRY_CODES,
  [PRODUCT_FAMILY.AD_INTEL]: COUNTRY_LIST.AD_INTEL_COUNTRY_CODES,
  [PRODUCT_FAMILY.SA_INTEL]: COUNTRY_LIST.SEARCH_ADS_INTEL_COUNTRY_CODES,
  [PRODUCT_FAMILY.CPP]: COUNTRY_LIST.SEARCH_ADS_INTEL_COUNTRY_CODES,
}

const PLAY_PRODUCT_FAMILY_MAPPING = {
  [PRODUCT_FAMILY.MARKET]: COUNTRY_LIST.PLAY_MARKET_INTEL_COUNTRY_CODES,
  [PRODUCT_FAMILY.ASO]: COUNTRY_LIST.PLAY_ASO_INTEL_COUNTRY_CODES,
  [PRODUCT_FAMILY.AD_INTEL]: COUNTRY_LIST.AD_INTEL_COUNTRY_CODES,
  [PRODUCT_FAMILY.SA_INTEL]: COUNTRY_LIST.SEARCH_ADS_INTEL_COUNTRY_CODES,
  [PRODUCT_FAMILY.CPP]: COUNTRY_LIST.SEARCH_ADS_INTEL_COUNTRY_CODES,
}

const IOS_SAFE_COUNTRY_MAPPING = COUNTRY_LIST.IOS_COUNTRY_CODES
const PLAY_SAFE_COUNTRY_MAPPING = COUNTRY_LIST.PLAY_COUNTRY_CODES

/**
 * Helper function for setting the store from payload.
 */
function resolveStore(payload) {
  return payload?.trackId ? getStoreName(payload.trackId) : payload?.store
}

/**
 * Determines which country codes to return based on the current route and store settings.
 *
 * @param {object} to - A route object that includes properties like name, matched routes, and meta data.
 * @param {object} options - An optional object containing store and/or trackId.
 * @param {string} options.store - A store identifier.
 * @param {string} options.trackId - A tracking identifier that can be used to determine the store.
 * @returns {Array} An array of country codes based on the given conditions.
 */
export function getCountryCodes(to, store) {
  let countries = COUNTRY_LIST.ALL_COUNTRY_CODES

  if (store && store != StoreWithAll.ALL) {
    const isIos = store == StoreWithAll.Ios
    const specialMapping = isIos ? IOS_SPECIAL_MAPPING : PLAY_SPECIAL_MAPPING
    const appIntelMapping = isIos ? IOS_APP_INTEL_DEFAULT_MAPPING : PLAY_APP_INTEL_DEFAULT_MAPPING
    const productMapping = isIos ? IOS_PRODUCT_FAMILY_MAPPING : PLAY_PRODUCT_FAMILY_MAPPING
    const safeCountryMapping = isIos ? IOS_SAFE_COUNTRY_MAPPING : PLAY_SAFE_COUNTRY_MAPPING

    countries = specialMapping[to.name]
      || (to.matched.some(({ name }) => name.includes(RouteName.APP_INTELLIGENCE)) && appIntelMapping)
      || (to.meta?.productFamily && productMapping[to.meta.productFamily])
      || safeCountryMapping
  }

  return countries
}

/**
 * Helper function that resolves a route by name and gives the corresponding country codes.
 *
 * @param {string} name - The name of the route.
 * @param {object} payload - Optional additional data (e.g., store or trackId) to influence the mapping.
 * @returns {Array} An array of country codes based on the resolved route.
 */
export function getCountryCodesByRouteName(name, payload = {}) {
  const to = router.resolve({ name })
  const store = resolveStore(payload)
  return getCountryCodes(to, store)
}
